import { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { adminChatWs } from '../services/chatWs';

type NavItem = { name: string; path: string; index: string; hint: string };

const navigation: NavItem[] = [
  { name: 'Overview', path: '/', index: '00', hint: 'Daily pulse' },
  { name: 'Applicants', path: '/requests', index: '01', hint: 'Inbound roster' },
  { name: 'Chats', path: '/chats', index: '02', hint: 'Live conversations' },
  { name: 'Documents', path: '/documents', index: '03', hint: 'Verification queue' },
  { name: 'Emails', path: '/emails', index: '04', hint: 'Outbound log' },
  { name: 'Users', path: '/users', index: '05', hint: 'Operators' },
  { name: 'Settings', path: '/settings', index: '06', hint: 'Workspace' },
  { name: 'Telegram', path: '/settings/telegram', index: '07', hint: 'Bot recipients' },
];

const currentLabel = (pathname: string): string => {
  const exact = navigation.find((n) => n.path === pathname);
  if (exact) return exact.name;
  if (pathname.startsWith('/requests')) return 'Applicants';
  if (pathname.startsWith('/chats')) return 'Chats';
  return 'Operations';
};

// Ported to the client's cool-grey + violet-260 palette. Names kept for
// backwards-compat with the JSX below; values mirror :root tokens in index.css.
const INK = '#262A35';
const PAPER = '#FBFBFC';
const AMBER = '#4D6DD8';
const RULE = '#DBDCE0';
const RULE_STRONG = '#C4C7CD';
const MUTED = '#8C8F98';

// Mirrors the client header logo (apps/client/src/components/Layout.tsx):
// same /logo.png mark + "DriverForce" in the display serif.
const Wordmark: React.FC = () => (
  <div className="flex items-center gap-2.5">
    <img
      src="/logo.png"
      alt=""
      width={28}
      height={28}
      className="w-7 h-7 rounded-full object-contain shrink-0"
    />
    <span
      className="font-display text-[22px] leading-none"
      style={{ color: '#fff', letterSpacing: '-0.02em' }}
    >
      DriverForce
    </span>
  </div>
);

// Shared between the desktop sidebar and the mobile drawer so the two never
// drift. `onNavigate` lets the drawer close itself when a link is chosen.
const SidebarContent: React.FC<{
  onNavigate?: () => void;
  accountSlot?: React.ReactNode;
}> = ({ onNavigate, accountSlot }) => (
  <>
    <div
      className="px-6 pt-7 pb-5"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
    >
      <Wordmark />
      <p
        className="mt-1 text-[11px] leading-snug"
        style={{ color: 'rgba(255,255,255,0.45)' }}
      >
        Logistics talent, triaged.
      </p>
    </div>

    <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-0.5">
      {navigation.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === '/'}
          onClick={onNavigate}
          className="group flex items-center gap-3 px-3 py-2.5 rounded-md transition"
          style={({ isActive }) => ({
            background: isActive ? 'rgba(255,255,255,0.06)' : 'transparent',
            color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
          })}
        >
          {({ isActive }) => (
            <>
              <span
                className="font-mono text-[10px] w-5"
                style={{
                  color: isActive ? AMBER : 'rgba(255,255,255,0.30)',
                }}
              >
                {item.index}
              </span>
              <span className="flex-1">
                <span className="block text-sm">{item.name}</span>
                <span
                  className="block text-[10px]"
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                >
                  {item.hint}
                </span>
              </span>
              {isActive && (
                <span
                  className="block w-1.5 h-1.5 rounded-full"
                  style={{ background: AMBER }}
                />
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>

    {accountSlot}

    <div
      className="px-6 py-4 font-mono text-[10px] uppercase overflow-hidden"
      style={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: '0.04em',
      }}
    >
      <div className="flex whitespace-nowrap gap-8 ticker">
        <span>fleet status · nominal</span>
        <span>matchmaker · idle</span>
        <span>otp · live</span>
        <span>queue · 0 jobs</span>
        <span>fleet status · nominal</span>
        <span>matchmaker · idle</span>
        <span>otp · live</span>
        <span>queue · 0 jobs</span>
      </div>
    </div>
  </>
);

export const AdminLayout: React.FC = () => {
  const { user, logout, token } = useAuth();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Keep the admin chat WS connected for the whole authenticated session.
  // ChatsPage uses the same socket to receive live broadcasts for every
  // conversation; mounting here means notifications survive route switches.
  useEffect(() => {
    adminChatWs.setToken(token ?? null);
    return () => {
      adminChatWs.setToken(null);
    };
  }, [token]);

  // Close the drawer on route change and on Escape; lock body scroll while open.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [navOpen]);

  const now = new Date();
  const dateLabel = now.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });

  const accountSlot = (
    <div
      className="px-6 py-4 lg:hidden"
      style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
    >
      <div
        className="font-mono text-[10px] uppercase"
        style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.04em' }}
      >
        Signed in as
      </div>
      <div className="mt-0.5 text-sm truncate" style={{ color: '#fff' }}>
        {user?.email}
      </div>
      <button
        onClick={logout}
        className="mt-3 w-full text-xs font-medium uppercase px-3 py-2 transition"
        style={{
          letterSpacing: '0.04em',
          border: '1px solid rgba(255,255,255,0.25)',
          color: PAPER,
          background: 'transparent',
        }}
      >
        Sign out
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ background: PAPER }}>
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-72 shrink-0 flex-col sticky top-0 h-screen"
        style={{ background: INK, color: PAPER }}
      >
        <SidebarContent />
      </aside>

      {/* Mobile drawer + backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${
          navOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
        onClick={() => setNavOpen(false)}
      />
      <aside
        id="mobile-nav"
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col transform transition-transform duration-200 ease-out ${
          navOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: INK, color: PAPER }}
        aria-label="Navigation"
        aria-hidden={!navOpen}
      >
        <button
          type="button"
          onClick={() => setNavOpen(false)}
          aria-label="Close navigation"
          className="absolute top-5 right-3 p-2"
          style={{ color: 'rgba(255,255,255,0.55)' }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M3 3l12 12M15 3L3 15" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>
        <SidebarContent onNavigate={() => setNavOpen(false)} accountSlot={accountSlot} />
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div
          className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-3 py-2.5"
          style={{ background: INK, color: PAPER }}
        >
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            aria-controls="mobile-nav"
            aria-expanded={navOpen}
            className="p-2 -ml-1"
            style={{ color: PAPER }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M2 5h16M2 10h16M2 15h16" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
          <Wordmark />
        </div>

        <header
          className="px-4 sm:px-6 lg:px-10 pt-5 lg:pt-8 pb-4 lg:pb-6"
          style={{ borderBottom: `1px solid ${RULE}` }}
        >
          <div className="flex items-end justify-between gap-4 lg:gap-6">
            <div className="min-w-0">
              <p
                className="font-mono text-[11px] uppercase"
                style={{ color: MUTED, letterSpacing: '0.04em' }}
              >
                {dateLabel}
              </p>
              <h1
                className="mt-1 font-display text-3xl lg:text-4xl truncate"
                style={{ color: INK, letterSpacing: '-0.015em' }}
              >
                {currentLabel(location.pathname)}
              </h1>
            </div>
            <div className="hidden md:flex items-center gap-4 shrink-0">
              <div className="text-right leading-tight">
                <div className="text-xs" style={{ color: MUTED }}>
                  Signed in as
                </div>
                <div className="text-sm font-medium" style={{ color: INK }}>
                  {user?.email}
                </div>
              </div>
              <button
                onClick={logout}
                className="text-xs font-medium uppercase px-3 py-2 transition"
                style={{
                  letterSpacing: '0.04em',
                  border: `1px solid ${RULE_STRONG}`,
                  color: INK,
                  background: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = INK;
                  e.currentTarget.style.color = PAPER;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = INK;
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
